import { SetStateAction, useContext, useState } from 'react';

import { useMutation } from '@apollo/client';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import { DELETE_USER_CREATED_RECIPE } from '../../app/graphql/mutations';
import { RecipeState } from '../../app/recipePage/recipePage.types';
import useLogRecipeEdit from '../../hooks/useLogRecipeEdit';
import { ErrorManagementContext } from '../../utils/errorManagement/ErrorManagementProvider';
import { EventLogsEnum } from '../../utils/events';
import styles from './recipeDeleteDialog.module.css';

const RecipeDeleteDialog = ({ deleteRecipePopup, setDeleteRecipePopup, recipe, onBack }: { deleteRecipePopup: boolean, setDeleteRecipePopup: React.Dispatch<SetStateAction<boolean>>, recipe: RecipeState, onBack: () => Promise<void> }) => {
    const { logRecipeEdit } = useLogRecipeEdit();
    const [updateRecipeData] = useMutation(DELETE_USER_CREATED_RECIPE);
    const handleClose = () => setDeleteRecipePopup(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const { cookClubLog } = useContext(ErrorManagementContext);

    const onRecipeDelete = async () => {
        try {
            setIsDeleting(true);
            logRecipeEdit(EventLogsEnum.APP_RECIPE_DELETE_CONFIRM);

            await updateRecipeData({
                variables: { recipeId: recipe?.id },
            });

        } catch (error) {
            console.error("Error deleting recipe:", error);
            cookClubLog(`Something went wrong: Please try again.`, "", {}, { ui: { type: "error" }, browserConsole: true });
        } finally {
            setIsDeleting(false);
            handleClose();
            onBack();
        }
    };

    return (
        <Dialog
            id={`delete-dialog-recipe-${recipe.id}`}
            onClose={handleClose}
            open={deleteRecipePopup}
            PaperProps={{ className: styles.delete_dialog_box }}
        >
            <Grid>
                <DialogTitle className={styles.dialog_title}>
                    <Typography variant='textBold'>Delete recipe?</Typography>
                </DialogTitle>
                <DialogContent className={styles.dialog_content_description}>
                    <Typography variant='caption3Light'>
                        Are you sure you want to delete {recipe.metadata.name}?
                    </Typography>
                </DialogContent>
                <DialogActions className={styles.dialog_actions}>
                    <Button className={styles.cancel_btn} id={`delete-cancel-btn-recipe-${recipe.id}`} onClick={handleClose}>
                        <Typography variant='caption3Light'>Cancel</Typography>
                    </Button>
                    <Button
                        className={isDeleting ? styles.delete_btn_disabled : styles.delete_btn}
                        disabled={isDeleting}
                        id={`delete-confirm-btn-recipe-${recipe.id}`}
                        onClick={onRecipeDelete}
                    >
                        <Typography variant="caption3Light">Delete</Typography>
                        {isDeleting && <div className={styles.loader_div}><CircularProgress className={styles.loader} size={17} /></div>}
                    </Button>
                </DialogActions>
            </Grid>
        </Dialog>
    );
};

export default RecipeDeleteDialog;
